import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Avatar,
    TextField,
    Button,
    Divider,
    Paper,
    CircularProgress,
    Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import moment from 'moment';
import 'moment-timezone';

// Estilos personalizados
const CommentContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1 // Añadido para asegurar que ocupe todo el espacio disponible
}));

const CommentsScrollArea = styled(Box)(({ theme }) => ({
    // Eliminamos maxHeight para que ocupe todo el espacio disponible
    height: '100%', // Usar toda la altura disponible
    overflowY: 'auto',
    marginBottom: theme.spacing(1), // Reducido de 2 a 1
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column', // Mostrar los más antiguos arriba y los más recientes abajo
    flex: 1, // Para que ocupe todo el espacio disponible
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.background.default,
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.divider,
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: theme.palette.action.hover,
    }
}));

const CommentItem = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5), // Reducido de 2 a 1.5
    marginBottom: theme.spacing(1.5), // Reducido de 2 a 1.5
    borderRadius: '12px', // Reducido de 16px a 12px
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
        boxShadow: '0 3px 8px rgba(0,0,0,0.09)'
    }
}));

const CommentHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1) // Reducido de 1.5 a 1
}));

const CommentForm = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1), // Reducido de 2 a 1
    marginTop: 'auto',
    padding: theme.spacing(1.5), // Reducido de 2 a 1.5
    borderRadius: '12px', // Reducido de 16px a 12px
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: 32, // Reducido de 36 a 32
    height: 32, // Reducido de 36 a 32
    marginRight: theme.spacing(1), // Reducido de 1.5 a 1
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    fontSize: '0.9rem' // Reducido de 1rem a 0.9rem
}));



const StatusChip = styled(Chip)(({ theme }) => ({
    borderRadius: '12px',
    fontSize: '0.7rem',
    height: '22px',
    fontWeight: 500,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    '& .MuiChip-label': {
        padding: '0 8px'
    }
}));

/**
 * Componente que muestra y permite agregar comentarios a un ticket
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.comments - Lista de comentarios del ticket
 * @param {Function} props.onAddComment - Función para agregar un nuevo comentario
 * @param {boolean} props.loading - Indica si se está cargando la información
 * @param {string} props.columnName - Nombre de la columna donde está el ticket
 * @returns {JSX.Element} Componente de comentarios
 */
const TicketComments = ({ comments = [], onAddComment, loading = false, columnName = '' }) => {
    const [newComment, setNewComment] = useState('');

    // Ordenar comentarios por fecha (más antiguos primero)
    const sortedComments = [...comments].sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA - dateB; // Orden cronológico (más antiguos primero)
    });

    // Usar todos los comentarios sin paginación
    const currentComments = sortedComments;

    // Efecto para desplazar al final del área de comentarios cuando se agregan comentarios
    useEffect(() => {
        if (commentsAreaRef.current) {
            // Desplazar al final para ver los comentarios más recientes
            commentsAreaRef.current.scrollTop = commentsAreaRef.current.scrollHeight;
        }
    }, [sortedComments.length]);

    // Manejador para cambios en el campo de texto
    const handleCommentChange = (e) => {
        setNewComment(e.target.value);
    };

    // Referencia para el área de comentarios
    const commentsAreaRef = React.useRef(null);

    // Manejador para enviar un nuevo comentario
    const handleSubmitComment = () => {
        if (newComment.trim()) {
            onAddComment(newComment.trim());
            setNewComment('');
        }
    };

    // Manejador para enviar comentario con Enter
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmitComment();
        }
    };

    // Formatear fecha para mostrar usando zona horaria de México
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment.utc(dateString).tz('America/Mexico_City').format('DD/MM/YYYY, HH:mm');
    };

    return (
        <CommentContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="500">
                    Comentarios
                </Typography>
                {sortedComments.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        {sortedComments.length} comentarios
                    </Typography>
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Lista de comentarios */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress size={28} color="primary" />
                </Box>
            ) : sortedComments.length > 0 ? (
                <>
                    <CommentsScrollArea ref={commentsAreaRef}>
                        {currentComments.map((comment, index) => (
                            <CommentItem key={comment.id || index}>
                                <CommentHeader>
                                    <StyledAvatar
                                        sx={{ bgcolor: 'primary.main' }}
                                    >
                                        {comment.author && comment.author[0].toUpperCase()}
                                    </StyledAvatar>
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="subtitle2" fontWeight="500">
                                                {comment.author || 'Agente'}
                                            </Typography>
                                            {comment.columnName && (
                                                <StatusChip
                                                    label={comment.columnName}
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            {formatDate(comment.date || new Date())}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: 1.5,
                                                color: 'text.primary'
                                            }}
                                        >
                                            {comment.text}
                                        </Typography>
                                    </Box>
                                </CommentHeader>
                            </CommentItem>
                        ))}
                    </CommentsScrollArea>

                    {/* Se ha eliminado la paginación */}
                </>
            ) : (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    opacity: 0.7
                }}>
                    <ChatIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        No hay comentarios para este ticket
                    </Typography>
                </Box>
            )}

            {/* Formulario para agregar comentarios */}
            <CommentForm>
                <TextField
                    fullWidth
                    multiline
                    rows={1} // Reducido de 2 a 1
                    placeholder="Escribe un comentario..."
                    variant="outlined"
                    value={newComment}
                    onChange={handleCommentChange}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    size="small" // Añadido para reducir el tamaño
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'background.default'
                        }
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {columnName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Carril actual:
                            </Typography>
                            <StatusChip
                                label={columnName}
                                size="small"
                            />
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<SendIcon />}
                        onClick={handleSubmitComment}
                        disabled={!newComment.trim() || loading}
                        size="small" // Añadido para reducir el tamaño
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 1.5, // Reducido de 2 a 1.5
                            py: 0.5 // Añadido para reducir la altura
                        }}
                    >
                        Enviar
                    </Button>
                </Box>
            </CommentForm>
        </CommentContainer>
    );
};

export default TicketComments;
